<template>
    <div>
        <vue-headful title="Equipment :: MeritEHS"/>

        <h4 class="py-3 mb-4">
            <ol class="breadcrumb font-weight-bold m-0">
                <li class="breadcrumb-item active"><i class="fa fa-truck"></i> Equipment</li>
                <li class="ml-3">
                    <router-link :to="`/equipment/create`" class="btn btn-sm btn-success" v-if="this.$root.hasPermission(['equipment.manage', 'equipment.create'])">
                        <i class="fa fa-plus-circle"></i> Create
                    </router-link>
                    <router-link :to="`/equipment/import`" class="btn btn-sm btn-primary ml-1" v-if="canManage">
                        <i class="fa fa-file-excel-o"></i> Import
                    </router-link>
                </li>
            </ol>
        </h4>

        <ul class="search-nav nav nav-tabs tabs-alt container-p-x container-m--x mb-4 d-lg-flex d-none">
            <b-nav-item :active="curTab === 'equipment'" @click="curTab = 'equipment'">
                Equipment List <template v-if="equipmentCount">({{ equipmentCount }})</template>
            </b-nav-item>
            <b-nav-item :active="curTab === 'equipment_dates'" @click="curTab = 'equipment_dates'">
                <i class="fa fa-calendar"></i> Equipment Dates <template v-if="dateCount">({{ dateCount }})</template>
            </b-nav-item>
            <b-nav-item :active="curTab === 'types'" @click="curTab = 'types'">
                Equipment Types <template v-if="typeCount">({{ typeCount }})</template>
            </b-nav-item>
        </ul>

        <div class="mb-2 mb-lg-0 d-lg-none d-block">
            <b-dropdown :html="getTabTitle" variant="default" class="bg-white">
                <b-dropdown-item :active="curTab === 'equipment'" @click="curTab = 'equipment'">
                    Equipment List <template v-if="equipmentCount">({{ equipmentCount }})</template>
                </b-dropdown-item>
                <b-dropdown-item :active="curTab === 'equipment_dates'" @click="curTab = 'equipment_dates'">
                    <i class="fa fa-calendar"></i> Equipment Dates <template v-if="dateCount">({{ dateCount }})</template>
                </b-dropdown-item>
                <b-dropdown-item :active="curTab === 'types'" @click="curTab = 'types'">
                    Equipment Types <template v-if="typeCount">({{ typeCount }})</template>
                </b-dropdown-item>
            </b-dropdown>
        </div>

        <div v-show="curTab === 'equipment'">
            <saved-filter-title :filterKey="'equipmentFilters'" />
        </div>

        <div class="card">
            <div class="card-body">
                <div v-show="curTab === 'equipment'">
                    <equipment-list ref="equipmentIndexList" @equipmentListCount="setEquipmentCount" :allowFilterSaving="true" :filterId="(filter_id ? parseInt(filter_id) : null)" />
                </div>

                <div v-show="curTab === 'equipment_dates'">

                    <div class="alert alert-info mb-2" v-if="show_date_help">
                        <h5>Upcoming Within Days:</h5>
                        <p>This filter will show all the following dates that are <strong>less than {{ days_ahead }} days from today</strong>. Use this to view upcoming or overdue dates.</p>
                        <ul>
                            <li>Calibration Date</li>
                            <li>Maintenance Date</li>
                            <li v-for="date in upcomingDates">
                                {{ date.label }}
                            </li>
                        </ul>

                        <template v-if="pastDates.length > 0">
                            <h5 class="mt-4">More Than Days Old:</h5>
                            <p>This filter will show all the following dates that are <strong>more than {{ days_behind }} days old from today</strong>. Use this to dates that are expired or about to expire, such as last oil changes. These dates can be created on the Equipment Type edit page by selected "Past" on the "Past or Future" custom date setting.</p>
                            <ul>
                                <li v-for="date in pastDates">
                                    {{ date.label }}
                                </li>
                            </ul>
                        </template>

                        <div class="mt-4">
                            <button class="btn btn-sm btn-link" v-on:click="show_date_help = !show_date_help">Hide Help</button>
                        </div>
                    </div>

                    <v-server-table ref="equipmentDateTable" url="/equipment/list" :columns="equipmentDateCols" :options="equipmentDateOptions">

                        <div slot="afterFilter">
                            <div class="form-inline">
                                <div class="form-group mr-3">
                                    <button class="btn btn-default" title="Show/Hide the Equipment Date Help" v-on:click="show_date_help = !show_date_help">
                                        <i class="fa fa-question-circle"></i>
                                    </button>
                                </div>
                                <b-input-group prepend="Upcoming Within" append="Days">
                                    <b-form-input v-model="days_ahead" placeholder="Days" style="max-width: 75px;" />
                                </b-input-group>

                                <template v-if="pastDates.length > 0">
                                    <div class="form-group ml-3 mr-3">
                                        Or
                                    </div>
                                    <b-input-group prepend="More Than" append="Days Old" class="ml-1">
                                        <b-form-input v-model="days_behind" placeholder="Days" style="max-width: 75px;" />
                                    </b-input-group>
                                </template>

                                <button class="btn btn-info ml-1" v-on:click="refreshDates()">
                                    Apply
                                </button>
                            </div>
                        </div>

                        <router-link slot="equipment_id" slot-scope="props" :to="`/equipment/view/${props.row.equipment_id}`" title="View Equipment">
                            {{ props.row.equipment_type_name }}
                            <span v-if="props.row.equipment_name">-</span> {{ props.row.equipment_name }}
                            <span v-if="props.row.model_number">-</span> {{ props.row.model_number }}
                            <span v-if="props.row.serial_number">-</span> {{ props.row.serial_number }}
                        </router-link>

                        <template slot="user_id" slot-scope="props">
                            {{ props.row.FirstName }} {{ props.row.LastName }}
                        </template>

                        <template slot="calibrate_date" slot-scope="props">
                            <span v-if="props.row.calibrate_date">{{ props.row.calibrate_date | moment("MM/DD/YYYY") }}</span>
                        </template>

                        <template slot="next_maintenance_date" slot-scope="props">
                            <span v-if="props.row.next_maintenance_date">{{ props.row.next_maintenance_date | moment("MM/DD/YYYY") }}</span>
                        </template>

                        <template slot="updated_at" slot-scope="props">
                            {{ props.row.updated_at | moment('timezone', userTimezone, "MM/DD/YYYY hh:mm a") }}
                        </template>

                    </v-server-table>
                </div>

                <div v-show="curTab === 'types'">
                    <v-server-table ref="equipmentTypeTable" url="/equipment/types/list" :columns="columns" :options="typeOptions">

                        <div slot="afterFilter" class="ml-2">
                            <router-link v-if="$store.state.user.is_admin" :to="`/equipment/types/edit`" class="btn btn btn-outline-success"><i class="fa fa-plus"></i> Add</router-link>
                        </div>

                        <template slot="equipment_type_id" slot-scope="props">
                            <router-link :to="`/equipment/types/view/${props.row.equipment_type_id}`" title="View Equipment Type" class="btn btn-sm btn-secondary">
                                View
                            </router-link>
                            <router-link v-if="$store.state.user.is_admin" :to="`/equipment/types/edit/${props.row.equipment_type_id}`" title="Edit Equipment Type" class="btn btn-sm btn-default">
                                <i class="fa fa-edit"></i> Edit
                            </router-link>
                        </template>

                        <router-link slot="equipment_type_name" slot-scope="props" :to="`/equipment/types/view/${props.row.equipment_type_id}`" title="View Equipment Type">
                            {{ props.row.equipment_type_name }}
                        </router-link>

                        <template slot="days_calibration_valid_for" slot-scope="props">
                            <span v-if="props.row.days_calibration_valid_for">{{ props.row.days_calibration_valid_for }} Days</span>
                        </template>

                        <template slot="require_inspection" slot-scope="props">
                            <label class="badge badge-success" v-if="props.row.require_inspection == 1">Yes</label>
                            <label class="badge badge-warning" v-else>No</label>
                        </template>

                        <template slot="require_insp_on_checkout" slot-scope="props">
                            <label class="badge badge-success" v-if="props.row.require_insp_on_checkout == 1">Yes</label>
                            <label class="badge badge-warning" v-else>No</label>
                        </template>

                        <template slot="restrict_checkout_w_open_ca" slot-scope="props">
                            <label class="badge badge-danger" v-if="props.row.restrict_checkout_w_open_ca == 1">Yes</label>
                            <label class="badge badge-default" v-else>No</label>
                        </template>

                        <template slot="require_tracking_jobsite" slot-scope="props">
                            <label class="badge badge-success" v-if="props.row.require_tracking_jobsite == 1">Yes</label>
                            <label class="badge badge-warning" v-else>No</label>
                        </template>

                        <template slot="use_absolute_usage" slot-scope="props">
                            <label class="badge badge-success" v-if="props.row.use_absolute_usage == 1">Yes</label>
                            <label class="badge badge-warning" v-else>No</label>
                        </template>

                        <template slot="is_active" slot-scope="props">
                            <label class="badge badge-success" v-if="props.row.is_active == 1">Active</label>
                            <label class="badge badge-warning" v-else>Inactive</label>
                        </template>

                    </v-server-table>
                </div>
            </div>
        </div>
    </div>
</template>

<style type="text/css">
    .input-group-text {
        background-color: rgba(24, 28, 33, 0.06) !important;
    }
</style>

<script>
    export default {
        data() {
            return {
                filter_id: this.$route.query.filter_id,
                curTab: 'equipment',
                show_date_help: false,
                equipmentCount: null,
                dateCount: null,
                typeCount: null,
                days_ahead: 30,
                days_behind: 180,
                userTimezone: this.$store.state.user.timezone,
                columns: [
                    'equipment_type_id',
                    'equipment_type_name',
                    'equipment_usage_type_name',
                    'days_calibration_valid_for',
                    'require_inspection',
                    'require_insp_on_checkout',
                    'restrict_checkout_w_open_ca',
                    'require_tracking_jobsite',
                    'use_absolute_usage',
                    'is_active'
                ],
                typeOptions: {
                    headings: {
                        equipment_type_id: 'View',
                        equipment_type_name: 'Type Name',
                        equipment_usage_type_name: 'Default Usage Type',
                        days_calibration_valid_for: 'Calibration Valid For',
                        require_inspection: 'Require Inspection on Check In',
                        require_insp_on_checkout: 'Require Inspection on Issuing',
                        restrict_checkout_w_open_ca: 'Prevent Checkout w/ Open Corrective Actions',
                        require_tracking_jobsite: 'Require Jobsite',
                        use_absolute_usage: 'Use Current Value',
                        is_active: 'Active',
                    },
                    // orderBy: {
                    //     ascending: true,
                    //     column: 'equipment_types.equipment_type_name',
                    // },
                    uniqueKey: 'equipment_type_id',
                    responseAdapter: (response) => {
                        //Emit the count
                        this.typeCount = response.data.count;

                        //Send it
                        return response.data;
                    },
                },
                equipment_dates: [],


            }
        },

        computed: {
            canManage(){
                return this.$root.hasPermission('equipment.manage');
            },

            upcomingDates(){
                //Setup
                var dates = [];

                //Loop and Build
                for (var i = this.equipment_dates.length - 1; i >= 0; i--)
                {
                    //Check for the value
                    if(!this.equipment_dates[i].past_or_future || this.equipment_dates[i].past_or_future == 'future')
                    {
                        dates.push(this.equipment_dates[i]);
                    }
                }

                //Send it
                return dates;
            },

            pastDates(){
                //Setup
                var dates = [];

                //Loop and Build
                for (var i = this.equipment_dates.length - 1; i >= 0; i--)
                {
                    //Check for the value
                    if(this.equipment_dates[i].past_or_future && this.equipment_dates[i].past_or_future == 'past')
                    {
                        dates.push(this.equipment_dates[i]);
                    }
                }

                //Send it
                return dates;
            },

            equipmentDateCols(){
                //Add the Columns
                var cols = [
                    'equipment_id',
                    'equipment_type_name',
                    'user_id',
                    'calibrate_date',
                    'next_maintenance_date',
                ];

                //Add the Custom Equipment Dates
                for (var i = this.equipment_dates.length - 1; i >= 0; i--)
                {
                    //Push it
                    cols.push(this.equipment_dates[i].id);
                }

                //Add the Last Updated
                cols.push('updated_at');

                //Send it
                return cols;
            },

            equipmentDateOptions(){
                //Build the Headings
                var headings = {
                   // equipment_date_id: 'ID',
                    equipment_id: 'Equipment',
                    equipment_type_name: 'Type',
                    user_id: 'Assignee',
                    calibrate_date: 'Calibrate Date',
                    next_maintenance_date: 'Maintenance Date',
                };

                //Add the Custom Equipment Dates
                for (var i = this.equipment_dates.length - 1; i >= 0; i--)
                {
                    //Push it
                    headings[this.equipment_dates[i].id] = this.equipment_dates[i].label;
                }

                //Close the Headings
                headings['updated_at'] = 'Last Updated';

                return {
                    uniqueKey: 'equipment_date_id',
                    headings: headings,
                    filterable: false,
                    //perPage: -1,
                    orderBy: {
                        ascending: false,
                        column: 'equipment.updated_at',
                    },
                    responseAdapter: (response) => {
                        //Emit the count
                        this.dateCount = response.data.count;

                        //Check if set
                        if(response.data.equipment_dates)
                        {
                            this.equipment_dates = response.data.equipment_dates;
                        }

                        //Send it
                        return response.data;
                    },
                    requestAdapter: (data) => {
                        //Set the Values
                        data.assetType = 'EquipmentDate';
                        data.days_ahead = this.days_ahead;
                        data.days_behind = this.days_behind;

                        //Send it
                        return data;
                    },
                };
            },

            getTabTitle(){
                //Setup
                var title = '';
                var icon = 'bars';

                //Switch
                switch(this.curTab)
                {
                    case 'equipment':
                        //Set
                        title = 'Equipment ('+this.equipmentCount+')';
                        break;
                    case 'equipment_dates':
                        //Set
                        title = 'Equipment Dates ('+this.dateCount+')';
                        icon = 'calendar';
                        break;
                    case 'types':
                        //Set
                        title = 'Equipment Types ('+this.typeCount+')';
                        break;
                }

                //Send it
                return '<i class="fa fa-'+icon+'"></i> ' + title;
            }
        },

        methods: {
            setEquipmentCount(count){
                //Set the count
                this.equipmentCount = count;
            },

            refreshDates(){
                this.$refs.equipmentDateTable.refresh();
            },
        },


        mounted(){
            //Check for a hash
            if(window.location.hash)
            {
                //Set the current tab
                this.curTab = window.location.hash.replace('#', '');
            }

            //Focus on the SEarch Input
            this.$refs.equipmentIndexList.$refs.equipmentTable.$refs.genericFilter.focus();
        }
    }
</script>
